import {
	ApolloClient,
	InMemoryCache,
	HttpLink,
	ApolloProvider
} from '@apollo/client'
import { authLink } from '../auth-link'
import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { useTestPlacesQuery, GQLTestPlacesQuery, useTestDatesQuery } from '../generated/graphql'
import { formatDateWithDayOfTheWeek, groupByDate, formatTime } from '../utils/util'
import { groupByArea, testAreaLabel } from '../constants/test-area'
import { createRoot } from 'react-dom/client'

document.addEventListener('DOMContentLoaded', () => {

	const client = new ApolloClient({
		cache: new InMemoryCache(),
		link: authLink.concat(new HttpLink({
			uri: document.getElementById('api-endpoint')?.dataset.apiEndpoint!,
			credentials: 'same-origin'
		}))
	})

  createRoot(document.getElementById('access')!).render(<ApolloProvider client={client}><TestPlaces /></ApolloProvider>)
})

type TestPlacesProps = {}
const TestPlaces: FunctionComponent<TestPlacesProps> = ({}) => {
  const testPlacesQuery = useTestPlacesQuery()
  const testPlaces = testPlacesQuery.data?.testPlaces
  const [hash, setHash] = useState(location.hash)
  
  if(!testPlaces) return <></>
  return <>
    <h2>検査会場</h2>
    <ul className="area-list">
      {
        groupByArea(testPlaces).map(([area, testPlaces]) => {
          if(testPlaces.length < 1) return null
          return <li key={area}>
            <p>{testAreaLabel[area]}</p>
            <ul className="table-of-contents">
              {
                testPlaces.sort((a, b) => a.order - b.order).map(testPlace => {
                  return <li key={testPlace.id}>
                    <a href={`#l-${testPlace.id}`}>{testPlace.name}</a>
                  </li>
                })
              }
            </ul>
          </li>
        })
      }
    </ul>
    <ul>
      {
        groupByArea(testPlaces).map(([area, testPlaces]) => testPlaces.sort((a, b) => a.order - b.order)).flat(1).map(testPlace => {
          return <Place key={testPlace.id} testPlace={testPlace} selected={hash === `#l-${testPlace.id}`} />
        })
      }
    </ul>
  </>
}

const Map: FunctionComponent<{testPlace: GQLTestPlacesQuery['testPlaces'][number]}> = ({testPlace}) => {
  const [show, setShow] = useState(false)
  if(show){
    return <div className="map" dangerouslySetInnerHTML={{__html: testPlace.embeddedMapHtml}}></div>
  } else {
    return <div className="show-map-button" onClick={() => setShow(show => !show)}>マップを表示する</div>
  }
  
}

const Place: FunctionComponent<{testPlace: GQLTestPlacesQuery['testPlaces'][number], selected?: boolean}> = ({testPlace, selected}) => {
  const containerRef = useRef<HTMLLIElement>(null)
  const testDatesQuery = useTestDatesQuery({
    variables: {
      testPlaceId: testPlace.id
    }
  })
  const testDates = testDatesQuery.data?.testDates
  useEffect(() => {
    if(selected){
      containerRef.current?.scrollIntoView()
    }
  }, [])
  return <li key={testPlace.id} id={`l-${testPlace.id}`} className="test-location-container" ref={containerRef}>
    <h3 className="name">{testPlace.name}</h3>
    <h4 className="clinic-name">{testPlace.clinicName}</h4>
    <p className="address">{testPlace.address}</p>
    <p className="access-method">{testPlace.access}</p>
    <p className="detail">{testPlace.detail}</p>

    <div className="available-test-courses">
      <h5>実施している検査コース</h5>
      <ul>
        {
          testPlace.acceptStandardFree &&
          <li>一般検査コース</li>
        }
        {
          testPlace.acceptStandardMb &&
          <li>継続研究参加コース</li>
        }
        {
          testPlace.acceptChueiBank &&
          <li>中栄信用金庫</li>
        }
        {
          testPlace.acceptKyofuku &&
          <li>神奈川県教育福祉振興会</li>
        }
        {
          testPlace.acceptRiebe &&
          <li>RIEBE会員特別コース</li>
        }
        {
          testPlace.acceptPromotion &&
          <li>各種イベントでのクーポンコード</li>
        }
        {
          testPlace.acceptSapporoChusho &&
          <li>公財 札幌市中小企業共済センター（さぽーとさっぽろ）会員特別コース</li>
        }
        {
          (testPlace.acceptBohseiMb || testPlace.acceptBohseiFree) &&
          <li>株式会社望星薬局社員特別コース</li>
        }
      </ul>
    </div>
    <p className="date-label">検査日時</p>
    <ul className="available-dates">
      {
        testDates ? 
        testDates.length > 0 ?
        Object.entries(groupByDate(testDates)).map(([dateNum, testDates]) => {
          const stopped = testDates.every(testDate => testDate.entryCount + testDate.otherPlatformEntryCount >= testDate.capacity || testDate.stopped)
          return <li key={dateNum}>
            <p style={{textDecoration: stopped ? 'line-through' : 'none'}}>{formatDateWithDayOfTheWeek(Number(dateNum))}</p>
            {
              testDates.every(testDate => testDate.entryCount + testDate.otherPlatformEntryCount >= testDate.capacity || testDate.stopped) &&
              `【${(testDates.map(td => td.stoppedMessage)?.[0] || '締め切りました')}】`
            }
          </li>
        }) :
        <li>無し</li> :
        '読み込み中…'
      }
    </ul>
    <Map testPlace={testPlace} />
  </li>
}